/**
 * useLockedPlanChoice.ts
 * 2026-07-10 silent-duplicate fix — shared orchestration for the locked-plan
 * choice (LockedPlanDialog).
 *
 * When resolveScenarioForSource returns { kind: 'locked' } (the source's only
 * plan is Submitted), BOTH resolve surfaces — the ScenarioPlanningPage
 * deep-link path (?source=&sourceKey=, reached from the quote editor's
 * "Edit Schedule" and the WO pages' "Add to Schedule") and the manual
 * ScenarioSourcePicker — must offer the same three explicit actions instead
 * of silently creating a duplicate draft. This hook owns that state machine
 * so the two surfaces can't drift:
 *
 *   openPlan  → hand the locked plan to the surface as-is (the scenario page
 *               renders Submitted plans read-only)
 *   reopen    → reopenScenario (Submitted → Draft, un-commits its rows) then
 *               re-fetch and hand over the now-editable plan
 *   createNew → resolveScenarioForSource(..., { forceCreate: true }) — the
 *               old auto-create, now an explicit choice
 *
 * The surface supplies how to "deliver" a scenario (set page state / call
 * onScenarioReady) and how to surface errors; failed actions keep the dialog
 * open so the user can pick another option.
 */

import { useState } from 'react';

import type { Scenario, ScenarioSourceType } from '../../../../services/scheduleScenario';
import { getScenario, reopenScenario } from '../../../../services/scheduleScenario';
import { resolveScenarioForSource } from './useResolveScenarioForSource';
import type { LockedPlanBusyAction } from './LockedPlanDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseLockedPlanChoiceArgs {
  /** Deliver a scenario to the surface (open it) + show a snackbar message. */
  onScenarioReady: (
    scenario: Scenario,
    message: string,
    severity: 'success' | 'info',
  ) => void;
  /** Surface an error message (dialog stays open on action failure). */
  onError: (message: string) => void;
  /** Called when the user cancels the dialog without choosing. */
  onDismiss?: () => void;
}

export interface LockedPlanChoice {
  /** The locked plan to render in the dialog (null = dialog closed). */
  lockedScenario: Scenario | null;
  dialogOpen: boolean;
  busy: LockedPlanBusyAction;
  /** Arm the dialog for a locked plan found while resolving a source. */
  show: (scenario: Scenario, sourceType: ScenarioSourceType, sourceKey: string) => void;
  handleOpenPlan: () => void;
  handleReopen: () => void;
  handleCreateNew: () => void;
  handleClose: () => void;
}

/** Extract the backend's error message from an axios-style error. */
function errorMessage(err: unknown, fallback: string): string {
  const apiMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (apiMsg) return apiMsg;
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLockedPlanChoice(args: UseLockedPlanChoiceArgs): LockedPlanChoice {
  const { onScenarioReady, onError, onDismiss } = args;

  const [locked, setLocked] = useState<{
    scenario: Scenario;
    sourceType: ScenarioSourceType;
    sourceKey: string;
  } | null>(null);
  const [busy, setBusy] = useState<LockedPlanBusyAction>(null);

  const show = (scenario: Scenario, sourceType: ScenarioSourceType, sourceKey: string) => {
    setBusy(null);
    setLocked({ scenario, sourceType, sourceKey });
  };

  // ── Open plan (read-only) ──────────────────────────────────────────────────
  const handleOpenPlan = () => {
    if (!locked || busy) return;
    const s = locked.scenario;
    setLocked(null);
    onScenarioReady(
      s,
      `Opened ${s.Code} — ${s.Name} (Scheduled — read-only; use "Reopen to Edit" to change it).`,
      'info',
    );
  };

  // ── Reopen to Edit (Submitted → Draft) ─────────────────────────────────────
  const handleReopen = () => {
    if (!locked || busy) return;
    setBusy('reopen');
    const key = locked.scenario.ScenarioKey;
    reopenScenario(key)
      .then(() => getScenario(key))
      .then((fresh) => {
        setLocked(null);
        setBusy(null);
        onScenarioReady(
          fresh,
          `Reopened ${fresh.Code} — ${fresh.Name} for editing. Its bookings are tentative again until resubmitted.`,
          'success',
        );
      })
      .catch((err: unknown) => {
        setBusy(null);
        onError(errorMessage(err, 'Failed to reopen the plan. Please try again.'));
      });
  };

  // ── Create new draft (explicit — the old auto-create) ─────────────────────
  const handleCreateNew = () => {
    if (!locked || busy) return;
    setBusy('create');
    resolveScenarioForSource(locked.sourceType, locked.sourceKey, { forceCreate: true })
      .then((result) => {
        if (result.kind !== 'resolved') {
          // Unreachable: forceCreate skips the locked check.
          setBusy(null);
          onError('Could not create a new draft. Please try again.');
          return;
        }
        setLocked(null);
        setBusy(null);
        onScenarioReady(result.scenario, result.message, result.severity);
      })
      .catch((err: unknown) => {
        setBusy(null);
        onError(errorMessage(err, 'Failed to create a new draft. Please try again.'));
      });
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (busy) return;
    setLocked(null);
    onDismiss?.();
  };

  return {
    lockedScenario: locked?.scenario ?? null,
    dialogOpen: locked !== null,
    busy,
    show,
    handleOpenPlan,
    handleReopen,
    handleCreateNew,
    handleClose,
  };
}
